/**
 * Gestion centralisée de la devise de la boutique active.
 *
 * La devise est lue depuis la boutique (champ `currency`) par DashboardLayout,
 * qui la mémorise ici. Les composants l'obtiennent à chaque rendu via
 * getDeviseActive() : toute modification ultérieure de la boutique est donc
 * prise en compte sans relire la liste des boutiques.
 */

const CLE_STOCKAGE = 'activeShopCurrency';
export const DEVISE_PAR_DEFAUT = 'XOF';

/**
 * Mémorise la devise de la boutique active (code ISO 4217 sur 3 lettres).
 * @param {string} [code] - Code devise ; une valeur invalide revient à la devise par défaut
 */
export const setDeviseActive = (code) => {
  const valide = /^[A-Za-z]{3}$/.test(code || '') ? code.toUpperCase() : DEVISE_PAR_DEFAUT;
  try {
    window.localStorage.setItem(CLE_STOCKAGE, valide);
  } catch (erreur) {
    console.error('Impossible de mémoriser la devise active:', erreur);
  }
};

/**
 * Retourne la devise de la boutique active.
 * @returns {string} Code devise (ex: "XOF", "EUR")
 */
export const getDeviseActive = () => {
  try {
    return window.localStorage.getItem(CLE_STOCKAGE) || DEVISE_PAR_DEFAUT;
  } catch (erreur) {
    return DEVISE_PAR_DEFAUT;
  }
};

/**
 * Formate un montant avec séparateurs français, suivi de la devise active.
 * @param {number|string} valeur - Montant brut
 * @param {number} [decimales] - Nombre de décimales fixes (aucune par défaut)
 * @returns {string} Ex: "15 000 XOF"
 */
export const formaterMontantDevise = (valeur, decimales) => {
  const options = decimales === undefined ? {} : { minimumFractionDigits: decimales, maximumFractionDigits: decimales };
  return `${Number(valeur || 0).toLocaleString('fr-FR', options)} ${getDeviseActive()}`;
};

/**
 * Crée un Intl.NumberFormat monétaire fr-FR dans la devise active.
 * @param {Object} [options] - Options Intl supplémentaires (ex: minimumFractionDigits)
 * @returns {Intl.NumberFormat}
 */
export const creerFormateurMonetaire = (options = {}) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: getDeviseActive(), ...options });

/**
 * Retourne la devise portée par une commande (pages publiques sans boutique active).
 * @param {Object} [commande] - Commande renvoyée par l'API, avec `currency`
 * @returns {string} Code devise, ou la devise par défaut si absente
 */
export const deviseCommande = (commande) => commande?.currency || DEVISE_PAR_DEFAUT;
